import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';

interface BonLaboratoire {
  id: string;
  patient_id: string;
  medecin_id: string | null;
  laborantin_id: string | null;
  sage_femme_id: string | null;
  prescripteur_type: string;
  type_analyse: string;
  urgencies: boolean;
  observations_cliniques: string | null;
  date_prescription: string;
  statut: string;
  resultats: string | null;
  date_resultat: string | null;
  created_at: string;
  patients: {
    id: string;
    nom: string;
    prenom: string;
    numero_dossier: string;
  } | null;
  medecins: {
    id: string;
    nom: string;
    prenom: string;
  } | null;
  laborantins: {
    id: string;
    nom: string;
    prenom: string;
  } | null;
  sage_femmes: {
    id: string;
    nom: string;
    prenom: string;
  } | null;
}

export default function AdminBonsLaboratoirePage() {
  const { user, role, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [bons, setBons] = useState<BonLaboratoire[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatut, setFilterStatut] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<BonLaboratoire | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login', { replace: true }); return; }
    if (role !== 'super_admin' && role !== 'admin') { navigate('/', { replace: true }); return; }
    loadBons();
  }, [user, role, authLoading]);

  const loadBons = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const { data, error } = await supabase
        .from('bons_laboratoire')
        .select(`
          *,
          patients(id, nom, prenom, numero_dossier),
          medecins(id, nom, prenom),
          laborantins(id, nom, prenom),
          sage_femmes(id, nom, prenom)
        `)
        .order('created_at', { ascending: false })
        .limit(500);

      if (error) {
        console.error('Erreur Supabase bons_laboratoire:', error);
        setLoadError(error.message || 'Erreur lors du chargement des bons');
        setBons([]);
        return;
      }

      if (data) setBons(data);
    } catch (err) {
      console.error('Erreur chargement bons laboratoire:', err);
      setLoadError(err instanceof Error ? err.message : 'Erreur inconnue');
      setBons([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = useCallback(async (bon: BonLaboratoire) => {
    setActionLoading(bon.id);
    try {
      const { error } = await supabase
        .from('bons_laboratoire')
        .delete()
        .eq('id', bon.id);

      if (error) throw error;

      setBons((prev) => prev.filter((b) => b.id !== bon.id));
      setDeleteTarget(null);
    } catch (err) {
      console.error('Erreur suppression:', err);
      alert('Erreur lors de la suppression du bon de laboratoire.');
    } finally {
      setActionLoading(null);
    }
  }, []);

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case 'en_attente':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-700 whitespace-nowrap">
            <i className="ri-time-line w-3 h-3 flex items-center justify-center"></i>
            En attente
          </span>
        );
      case 'en_cours':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700 whitespace-nowrap">
            <i className="ri-loader-4-line w-3 h-3 flex items-center justify-center"></i>
            En cours
          </span>
        );
      case 'termine':
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-700 whitespace-nowrap">
            <i className="ri-check-double-line w-3 h-3 flex items-center justify-center"></i>
            Terminé
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600 whitespace-nowrap">
            {statut}
          </span>
        );
    }
  };

  const getPrescripteurLabel = (bon: BonLaboratoire) => {
    if (bon.prescripteur_type === 'sage_femme' && bon.sage_femmes) {
      return `SF ${bon.sage_femmes.prenom} ${bon.sage_femmes.nom}`;
    }
    if (bon.medecins) {
      return `Dr. ${bon.medecins.nom} ${bon.medecins.prenom}`;
    }
    return 'Prescripteur inconnu';
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('fr-FR', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const filteredBons = bons.filter((bon) => {
    const search = searchTerm.toLowerCase();
    const patientMatch = bon.patients
      ? `${bon.patients.nom} ${bon.patients.prenom}`.toLowerCase().includes(search)
      : false;
    const dossierMatch = bon.patients?.numero_dossier?.toLowerCase().includes(search) || false;
    const prescripteurMatch = getPrescripteurLabel(bon).toLowerCase().includes(search);
    const analyseMatch = bon.type_analyse?.toLowerCase().includes(search) || false;

    if (search && !patientMatch && !dossierMatch && !prescripteurMatch && !analyseMatch) return false;
    if (filterStatut && bon.statut !== filterStatut) return false;
    return true;
  });

  const stats = {
    total: bons.length,
    enAttente: bons.filter((b) => b.statut === 'en_attente').length,
    enCours: bons.filter((b) => b.statut === 'en_cours').length,
    urgents: bons.filter((b) => b.urgencies).length,
    aujourdhui: bons.filter((b) => {
      const today = new Date().toISOString().split('T')[0];
      return b.created_at?.startsWith(today);
    }).length,
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-600 animate-spin mb-4 block"></i>
          <div className="text-gray-700 text-xl">Chargement...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin')}
                className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-gray-100 transition-all cursor-pointer"
              >
                <i className="ri-arrow-left-line text-xl text-gray-600"></i>
              </button>
              <div>
                <h1 className="text-3xl font-bold text-gray-900">Bons de Laboratoire</h1>
                <p className="mt-1 text-gray-600">
                  Consulter et supprimer les analyses en attente ou déjà traitées
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {/* Stats */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <div className="bg-white p-5 rounded-2xl border border-gray-100">
              <p className="text-sm text-gray-500 mb-1">Total</p>
              <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-amber-100">
              <p className="text-sm text-gray-500 mb-1">En attente</p>
              <p className="text-3xl font-bold text-amber-600">{stats.enAttente}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-blue-100">
              <p className="text-sm text-gray-500 mb-1">En cours</p>
              <p className="text-3xl font-bold text-blue-600">{stats.enCours}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-red-100">
              <p className="text-sm text-gray-500 mb-1">Urgents</p>
              <p className="text-3xl font-bold text-red-500">{stats.urgents}</p>
            </div>
            <div className="bg-white p-5 rounded-2xl border border-green-100">
              <p className="text-sm text-gray-500 mb-1">Aujourd&apos;hui</p>
              <p className="text-3xl font-bold text-green-600">{stats.aujourdhui}</p>
            </div>
          </div>

          {/* Filtres */}
          <div className="bg-white p-6 rounded-2xl border border-gray-100 mb-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Rechercher par patient, prescripteur, analyse ou n° dossier..."
                  className="w-full pl-11 pr-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm"
                />
              </div>
              <select
                value={filterStatut}
                onChange={(e) => setFilterStatut(e.target.value)}
                className="px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-teal-500 text-sm cursor-pointer min-w-[180px]"
              >
                <option value="">Tous les statuts</option>
                <option value="en_attente">En attente</option>
                <option value="en_cours">En cours</option>
                <option value="termine">Terminé</option>
              </select>
            </div>
          </div>

          {/* Liste */}
          <div className="bg-white p-8 rounded-3xl border border-gray-100">
            {loadError ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-error-warning-line text-4xl text-red-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Erreur de chargement</h3>
                <p className="text-red-500 mb-4">{loadError}</p>
                <button
                  onClick={loadBons}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-teal-600 text-white rounded-xl text-sm font-medium hover:bg-teal-700 transition-all cursor-pointer"
                >
                  <i className="ri-refresh-line w-4 h-4 flex items-center justify-center"></i>
                  Réessayer
                </button>
              </div>
            ) : filteredBons.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-20 h-20 bg-teal-50 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <i className="ri-microscope-line text-4xl text-teal-500"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun bon trouvé</h3>
                <p className="text-gray-500">Essayez de modifier vos filtres</p>
              </div>
            ) : (
              <div className="space-y-4">
                {filteredBons.map((bon) => (
                  <div
                    key={bon.id}
                    className={`p-6 rounded-2xl border-2 transition-all ${
                      bon.urgencies
                        ? 'border-red-300 bg-red-50/20'
                        : bon.statut === 'en_attente'
                          ? 'border-amber-200 bg-amber-50/20'
                          : 'border-gray-200 hover:border-teal-500/20'
                    }`}
                  >
                    <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              bon.urgencies
                                ? 'bg-red-500'
                                : bon.statut === 'en_attente'
                                  ? 'bg-amber-500'
                                  : 'bg-blue-500'
                            }`}
                          >
                            <i
                              className={`text-lg text-white ${
                                bon.urgencies ? 'ri-alert-fill' : 'ri-flask-line'
                              }`}
                            ></i>
                          </div>
                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-lg font-bold text-gray-900">
                                {bon.patients
                                  ? `${bon.patients.prenom} ${bon.patients.nom}`
                                  : 'Patient supprimé'}
                              </span>
                              {bon.patients?.numero_dossier && (
                                <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">
                                  N°{bon.patients.numero_dossier}
                                </span>
                              )}
                              {bon.urgencies && (
                                <span className="inline-flex items-center gap-1 text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded-full font-semibold">
                                  <i className="ri-alert-fill w-3 h-3 flex items-center justify-center"></i>
                                  URGENT
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-0.5">
                              Prescrit par : {getPrescripteurLabel(bon)}
                            </p>
                          </div>
                        </div>

                        <div className="ml-13 grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div className="flex items-start gap-2">
                            <i className="ri-flask-line text-teal-500 w-4 h-4 flex items-center justify-center mt-0.5"></i>
                            <p className="text-sm text-gray-700">
                              <strong>Analyses :</strong>{' '}
                              <span className="text-gray-600">{bon.type_analyse || 'Non spécifié'}</span>
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            <i className="ri-calendar-line text-teal-500 w-4 h-4 flex items-center justify-center"></i>
                            <p className="text-sm text-gray-700">
                              <strong>Prescrit le :</strong>{' '}
                              {bon.date_prescription ? formatDate(bon.date_prescription) : '—'}
                            </p>
                          </div>
                          {bon.laborantins && (
                            <div className="flex items-center gap-2">
                              <i className="ri-user-star-line text-teal-500 w-4 h-4 flex items-center justify-center"></i>
                              <p className="text-sm text-gray-700">
                                <strong>Laborantin :</strong>{' '}
                                {bon.laborantins.prenom} {bon.laborantins.nom}
                              </p>
                            </div>
                          )}
                          {bon.observations_cliniques && (
                            <div className="flex items-start gap-2 md:col-span-2">
                              <i className="ri-file-text-line text-gray-400 w-4 h-4 flex items-center justify-center mt-0.5"></i>
                              <p className="text-sm text-gray-500 line-clamp-2">
                                {bon.observations_cliniques}
                              </p>
                            </div>
                          )}
                          {bon.resultats && (
                            <div className="flex items-start gap-2 md:col-span-2">
                              <i className="ri-check-double-line text-green-500 w-4 h-4 flex items-center justify-center mt-0.5"></i>
                              <p className="text-sm text-gray-600 line-clamp-2">
                                <strong>Résultats :</strong> {bon.resultats}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-3">
                        {getStatutBadge(bon.statut)}
                        <div className="text-xs text-gray-400">
                          Créé le {formatDateTime(bon.created_at)}
                        </div>

                        <div className="flex flex-wrap gap-2 justify-end">
                          <button
                            onClick={() => setDeleteTarget(bon)}
                            disabled={actionLoading === bon.id}
                            className="flex items-center gap-1 px-4 py-2 bg-red-50 text-red-700 rounded-full text-sm font-medium hover:bg-red-100 transition-all whitespace-nowrap cursor-pointer disabled:opacity-50"
                          >
                            {actionLoading === bon.id ? (
                              <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                            ) : (
                              <i className="ri-delete-bin-line w-4 h-4 flex items-center justify-center"></i>
                            )}
                            Supprimer
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setDeleteTarget(null)}
          ></div>
          <div className="relative z-10 mx-4 w-full max-w-md overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-lg">
            <div className="h-1.5 w-full bg-red-500"></div>
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-red-100">
                  <i className="ri-delete-bin-line text-xl text-red-600"></i>
                </span>
                <h3 className="text-lg font-bold text-gray-900">Confirmer la suppression</h3>
              </div>

              <div className="mb-2 rounded-xl bg-red-50 p-4">
                <p className="text-sm text-gray-700">
                  <strong>Patient :</strong>{' '}
                  {deleteTarget.patients
                    ? `${deleteTarget.patients.prenom} ${deleteTarget.patients.nom}`
                    : 'Patient supprimé'}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Prescripteur :</strong> {getPrescripteurLabel(deleteTarget)}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Analyses :</strong> {deleteTarget.type_analyse || 'Non spécifié'}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Statut :</strong>{' '}
                  {deleteTarget.statut === 'en_attente' ? 'En attente' : deleteTarget.statut === 'termine' ? 'Terminé' : 'En cours'}
                </p>
                <p className="text-sm text-gray-700 mt-1">
                  <strong>Date prescription :</strong>{' '}
                  {deleteTarget.date_prescription
                    ? new Date(deleteTarget.date_prescription + 'T00:00:00').toLocaleDateString('fr-FR')
                    : '—'}
                </p>
              </div>

              <p className="text-sm text-red-600 font-medium mb-6">
                Cette action est irréversible. Le bon sera définitivement supprimé de la base de
                données.
              </p>

              <div className="flex gap-3">
                <button
                  onClick={() => setDeleteTarget(null)}
                  disabled={actionLoading === deleteTarget.id}
                  className="flex-1 rounded-xl border border-gray-200 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  Annuler
                </button>
                <button
                  onClick={() => handleDelete(deleteTarget)}
                  disabled={actionLoading === deleteTarget.id}
                  className="flex-1 rounded-xl bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-60 whitespace-nowrap cursor-pointer"
                >
                  {actionLoading === deleteTarget.id ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin w-4 h-4 flex items-center justify-center"></i>
                      Suppression...
                    </span>
                  ) : (
                    'Supprimer définitivement'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}